// Bottom bar shown in selection mode: count, optional extra actions, delete with confirm.
import React from 'react';
import { Text, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useTranslation } from 'react-i18next';
import { IconButton } from './ui';
import { FadeIn } from './Motion';
import { confirm, showMenu, type MenuItem } from './Dialogs';
import { font, radius, spacing, usePalette } from '../theme';

export default function SelectionBar({ count, onDelete, extra }: { count: number; onDelete: () => void; extra?: MenuItem[] }) {
  const p = usePalette();
  const { t } = useTranslation();
  const insets = useSafeAreaInsets();
  return (
    <FadeIn
      style={{
        position: 'absolute', left: spacing.l, right: spacing.l, bottom: insets.bottom + spacing.m, flexDirection: 'row', alignItems: 'center',
        gap: spacing.m, backgroundColor: p.elevated, borderRadius: radius.pill, paddingHorizontal: spacing.l, paddingVertical: spacing.s,
        borderWidth: 1, borderColor: p.border, // matte: an outline, no drop shadow
      }}>
      {extra?.length ? <IconButton icon="plus" label={t('library.addSelected')} disabled={count === 0} onPress={() => showMenu(extra, t('library.addSelected'))} /> : <View style={{ width: 32 }} />}
      <Text style={{ flex: 1, textAlign: 'center', fontSize: font.body, fontWeight: '600', color: p.text }}>{t('common.selected', { count })}</Text>
      <IconButton
        icon="trash"
        color={p.danger}
        label={t('common.deleteSelection')}
        disabled={count === 0}
        onPress={async () => {
          if (await confirm(t('common.deleteConfirmTitle'), t('common.deleteConfirmBody'), t('common.deleteSelection'))) onDelete();
        }}
      />
    </FadeIn>
  );
}

/** Small hook for Set-based selection state. */
export function useSelection() {
  const [editing, setEditing] = React.useState(false);
  const [ids, setIds] = React.useState<Set<string>>(new Set());
  return {
    editing,
    ids,
    start: () => setEditing(true),
    stop: () => {
      setEditing(false);
      setIds(new Set());
    },
    toggle: (id: string) => setIds(s => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    }),
    setAll: (all: string[]) => setIds(s => (s.size === all.length ? new Set() : new Set(all))),
    has: (id: string) => ids.has(id),
  };
}
